import HelpMenu from '@components/Help/HelpMenu';
import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useMediaQuery,
  useTheme,
  Tooltip,
  Chip,
  alpha,
  Switch,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Person,
  Description,
  LocalHospital,
  Science,
  Biotech,
  Share,
  AccountCircle,
  Logout,
  DarkMode,
  LightMode,
  Settings,
  CardMembership,
  EventNote,
  CalendarMonth,
  PeopleAlt,
  Business,
  SupervisorAccount,
  Assessment,
  AdminPanelSettings,
  Security,
  Help,
  ExpandMore,
  Medication,
  Vaccines,
  AccessTime,
  Search as SearchIcon,
  Tv,
  CurrencyRupee,
  LocalPharmacy,
  BugReport,
} from '@mui/icons-material';
import { useAuth } from '@context/AuthContext';
import { UserRole } from '../types';
import NotificationBell from '@components/NotificationBell';
import GlobalSearch from '@components/Search/GlobalSearch';
import { usePreferences } from '@context/PreferencesContext';

// Menu labels → translation keys (labels without a key stay in English)
const NAV_KEYS: Record<string, string> = {
  Dashboard: 'nav.dashboard',
  Appointments: 'nav.appointments',
  Scheduling: 'nav.scheduling',
  Billing: 'nav.billing',
  Pharmacy: 'nav.pharmacy',
  'Waiting Room Screen': 'nav.waitingRoom',
  Patients: 'nav.patients',
  Vaccinations: 'nav.vaccinations',
  Prescriptions: 'nav.prescriptions',
  'Lab Reports': 'nav.labReports',
  'My Availability': 'nav.myAvailability',
  'Medicine Database': 'nav.medicines',
  Staff: 'nav.staff',
  'Hospital Settings': 'nav.hospitalSettings',
  Branches: 'nav.branches',
  'API Keys': 'nav.apiKeys',
  'Organization Metrics': 'nav.orgMetrics',
  'Audit Log': 'nav.auditLog',
  'New Report': 'nav.newReport',
  'DICOM Studies': 'nav.dicom',
};
import { OrganizationSwitcher, SupportModeBanner } from '@components/Organization/OrganizationSwitcher';

const DRAWER_WIDTH = 280;

const DashboardLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t, mode, setThemeChoice } = usePreferences();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

  // Global shortcuts: Ctrl/⌘+K opens the command palette; Shift+N starts a prescription (doctors)
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((open) => !open);
        return;
      }
      const target = e.target as HTMLElement | null;
      const typing = !!target && (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable);
      if (!typing && e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey && e.key === 'N' && user?.role === UserRole.DOCTOR) {
        e.preventDefault();
        navigate('/prescriptions/create');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate, user?.role]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getRoleDisplayName = (role: string | undefined) => {
    if (!role) return 'User';
    const roleMap: { [key: string]: string } = {
      [UserRole.DOCTOR]: 'Doctor',
      [UserRole.PATIENT]: 'Patient',
      [UserRole.RECEPTIONIST]: 'Receptionist',
      [UserRole.LAB_TECHNICIAN]: 'Lab Technician',
      [UserRole.RADIOLOGIST]: 'Radiologist',
      [UserRole.ADMIN]: 'Admin',
      [UserRole.SUPERADMIN]: 'Super Admin',
    };
    return roleMap[role] || role;
  };

  const getRoleColor = (role: string | undefined) => {
    if (!role) return '#9e9e9e';
    const colorMap: { [key: string]: string } = {
      [UserRole.DOCTOR]: '#2196f3',
      [UserRole.PATIENT]: '#4caf50',
      [UserRole.RECEPTIONIST]: '#ff9800',
      [UserRole.LAB_TECHNICIAN]: '#9c27b0',
      [UserRole.RADIOLOGIST]: '#00bcd4',
      [UserRole.ADMIN]: '#f44336',
      [UserRole.SUPERADMIN]: '#e91e63',
    };
    return colorMap[role] || '#9e9e9e';
  };

  const menuItems = React.useMemo(() => {
    const items = [
      { text: 'Dashboard', icon: <Dashboard />, path: '/', roles: ['all'] },
    ];

    if (user?.role === UserRole.PATIENT) {
      items.push(
        { text: 'My Profile', icon: <Person />, path: '/profile', roles: [UserRole.PATIENT] },
        { text: 'ABHA Registration', icon: <CardMembership />, path: '/abha-register', roles: [UserRole.PATIENT] },
        { text: 'Data Sharing', icon: <Share />, path: '/data-sharing/consents', roles: [UserRole.PATIENT] }
      );
    }

    if (user?.role === UserRole.DOCTOR) {
      items.push(
        { text: 'Appointments', icon: <EventNote />, path: '/appointments', roles: [UserRole.DOCTOR] },
        { text: 'Prescriptions', icon: <Description />, path: '/prescriptions', roles: [UserRole.DOCTOR] },
        { text: 'Patients', icon: <LocalHospital />, path: '/patients', roles: [UserRole.DOCTOR] },
        { text: 'Lab Reports', icon: <Science />, path: '/lab-reports', roles: [UserRole.DOCTOR] },
        { text: 'My Availability', icon: <AccessTime />, path: '/doctor/availability', roles: [UserRole.DOCTOR] },
        { text: 'Medicine Database', icon: <Medication />, path: '/medicines', roles: [UserRole.DOCTOR] },
        { text: 'Vaccinations', icon: <Vaccines />, path: '/vaccinations', roles: [UserRole.DOCTOR] }
      );
    }

    if (user?.role === UserRole.LAB_TECHNICIAN || user?.role === UserRole.PATHOLOGIST) {
      items.push(
        { text: 'Lab Reports', icon: <Science />, path: '/lab-reports', roles: [UserRole.LAB_TECHNICIAN, UserRole.PATHOLOGIST] },
        { text: 'New Report', icon: <Description />, path: '/lab-reports/create', roles: [UserRole.LAB_TECHNICIAN, UserRole.PATHOLOGIST] }
      );
    }

    if (user?.role === UserRole.RADIOLOGIST) {
      items.push(
        { text: 'DICOM Studies', icon: <Biotech />, path: '/dicom/studies', roles: [UserRole.RADIOLOGIST] }
      );
    }

    if (user?.role === UserRole.RECEPTIONIST) {
      items.push(
        { text: 'Appointments', icon: <EventNote />, path: '/appointments', roles: [UserRole.RECEPTIONIST] },
        { text: 'Scheduling', icon: <CalendarMonth />, path: '/receptionist/scheduling', roles: [UserRole.RECEPTIONIST] },
        { text: 'Billing', icon: <CurrencyRupee />, path: '/billing', roles: [UserRole.RECEPTIONIST] },
        { text: 'Pharmacy', icon: <LocalPharmacy />, path: '/pharmacy', roles: [UserRole.RECEPTIONIST] },
        { text: 'Waiting Room Screen', icon: <Tv />, path: '/queue-display', roles: [UserRole.RECEPTIONIST] },
        { text: 'Patients', icon: <PeopleAlt />, path: '/receptionist/patients', roles: [UserRole.RECEPTIONIST] },
        { text: 'Vaccinations', icon: <Vaccines />, path: '/vaccinations', roles: [UserRole.RECEPTIONIST] }
      );
    }

    if (user?.role === UserRole.SUPERADMIN || (user?.role as string) === 'superadmin') {
      items.push(
        { text: 'Organizations', icon: <Business />, path: '/superadmin/organizations', roles: [UserRole.SUPERADMIN] },
        { text: 'All Users', icon: <SupervisorAccount />, path: '/superadmin/users', roles: [UserRole.SUPERADMIN] },
        { text: 'System Metrics', icon: <Assessment />, path: '/superadmin/metrics', roles: [UserRole.SUPERADMIN] },
        { text: 'Problem Reports', icon: <BugReport />, path: '/superadmin/reports', roles: [UserRole.SUPERADMIN] },
        { text: 'Medicine Database', icon: <Medication />, path: '/medicines', roles: [UserRole.SUPERADMIN] }
      );
    }

    if (user?.role === UserRole.ADMIN || (user?.role as string) === 'admin') {
      items.push(
        { text: 'Staff', icon: <PeopleAlt />, path: '/admin/users', roles: [UserRole.ADMIN] },
        { text: 'Hospital Settings', icon: <Settings />, path: '/admin/settings', roles: [UserRole.ADMIN] },
        { text: 'Branches', icon: <Business />, path: '/admin/branches', roles: [UserRole.ADMIN] },
        { text: 'API Keys', icon: <Security />, path: '/admin/api-keys', roles: [UserRole.ADMIN] },
        { text: 'Organization Metrics', icon: <Assessment />, path: '/admin/metrics', roles: [UserRole.ADMIN] },
        { text: 'Audit Log', icon: <Security />, path: '/admin/audit-logs', roles: [UserRole.ADMIN] },
        { text: 'Billing', icon: <CurrencyRupee />, path: '/billing', roles: [UserRole.ADMIN] },
        { text: 'Pharmacy', icon: <LocalPharmacy />, path: '/pharmacy', roles: [UserRole.ADMIN] },
        { text: 'Lab Reports', icon: <Science />, path: '/lab-reports', roles: [UserRole.ADMIN] },
        { text: 'Medicine Database', icon: <Medication />, path: '/medicines', roles: [UserRole.ADMIN] },
        { text: 'Vaccinations', icon: <Vaccines />, path: '/vaccinations', roles: [UserRole.ADMIN] }
      );
    }

    return items;
  }, [user?.role]);

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo Section */}
      <Box 
        sx={{ 
          p: 3, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
      >
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
          <LocalHospital sx={{ color: 'white', fontSize: 28 }} />
        </Avatar>
        <Box>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, letterSpacing: 0.5 }}>
            MedEase
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Healthcare Platform
          </Typography>
        </Box>
      </Box>
      
      {/* User Info Section */}
      {/* <Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar 
            src={user?.profilePhoto}
            sx={{ 
              width: 44, 
              height: 44, 
              bgcolor: getRoleColor(user?.role),
              border: '2px solid',
              borderColor: alpha(getRoleColor(user?.role), 0.3)
            }}
          >
            {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={600} noWrap>
              {user?.firstName} {user?.lastName}
            </Typography>
            <Chip 
              label={getRoleDisplayName(user?.role)} 
              size="small" 
              sx={{ 
                height: 20, 
                fontSize: '0.7rem',
                bgcolor: alpha(getRoleColor(user?.role), 0.1),
                color: getRoleColor(user?.role),
                fontWeight: 600
              }} 
            />
          </Box>
        </Box>
      </Box> */}

      <Divider />
      
      {/* Navigation Items */}
      <List sx={{ flex: 1, py: 1 }}>
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path || 
                          (item.path !== '/' && location.pathname.startsWith(item.path));
          return (
            <ListItem key={item.text} disablePadding sx={{ px: 1, mb: 0.5 }}>
              <ListItemButton
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setMobileOpen(false);
                }}
                sx={{
                  borderRadius: 2,
                  py: 1.2,
                  bgcolor: isActive ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                  color: isActive ? 'primary.main' : 'text.primary',
                  '&:hover': {
                    bgcolor: isActive 
                      ? alpha(theme.palette.primary.main, 0.15)
                      : alpha(theme.palette.primary.main, 0.05),
                  },
                  '& .MuiListItemIcon-root': {
                    color: isActive ? 'primary.main' : 'text.secondary',
                    minWidth: 40,
                  },
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText 
                  primary={NAV_KEYS[item.text] ? t(NAV_KEYS[item.text]) : item.text} 
                  primaryTypographyProps={{ 
                    fontWeight: isActive ? 600 : 400,
                    fontSize: '0.9rem'
                  }}
                />
                {isActive && (
                  <Box 
                    sx={{ 
                      width: 4, 
                      height: 24, 
                      bgcolor: 'primary.main', 
                      borderRadius: 2,
                      ml: 1
                    }} 
                  />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Bottom Section */}
      <Divider />
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
          © 2026 MedEase v1.0
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ display: { md: 'none' }, color: 'text.primary' }}
            >
              <MenuIcon />
            </IconButton>
            <Box>
              <Typography variant="h6" color="text.primary" fontWeight={600} noWrap>
                {user?.role === UserRole.DOCTOR && 'Doctor Dashboard'}
                {user?.role === UserRole.PATIENT && 'Patient Portal'}
                {user?.role === UserRole.LAB_TECHNICIAN && 'Lab Technician Portal'}
                {user?.role === UserRole.RADIOLOGIST && 'Radiologist Portal'}
                {user?.role === UserRole.RECEPTIONIST && 'Receptionist Portal'}
                {user?.role === UserRole.ADMIN && (user?.supportMode ? `${user.organizationName} — Admin Portal` : 'Admin Portal')}
                {(user?.role === UserRole.SUPERADMIN || (user?.role as string) === 'superadmin') && 'Super Admin Portal'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <OrganizationSwitcher />
            {/* Command palette */}
            <Button
              onClick={() => setPaletteOpen(true)}
              startIcon={<SearchIcon />}
              sx={{
                color: 'text.secondary',
                border: 1,
                borderColor: 'divider',
                textTransform: 'none',
                display: { xs: 'none', sm: 'inline-flex' },
              }}
            >
              {t('top.search')}
              <Box component="kbd" sx={{ ml: 1.5, px: 0.75, fontSize: 11, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                {isMac ? '⌘K' : 'Ctrl K'}
              </Box>
            </Button>
            <IconButton
              onClick={() => setPaletteOpen(true)}
              sx={{ color: 'text.secondary', display: { xs: 'inline-flex', sm: 'none' } }}
            >
              <SearchIcon />
            </IconButton>

            {/* Notifications */}
            <NotificationBell />

            {/* Help */}
            <HelpMenu />

            <Divider orientation="vertical" flexItem sx={{ mx: 1, my: 1 }} />

            {/* User Menu Button */}
            <Box 
              onClick={handleProfileMenuOpen}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5, 
                cursor: 'pointer',
                p: 0.5,
                pl: 1.5,
                pr: 1,
                borderRadius: 3,
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.05)
                }
              }}
            >
              <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right' }}>
                <Typography variant="body2" fontWeight={600} color="text.primary" noWrap>
                  {user?.firstName} {user?.lastName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {getRoleDisplayName(user?.role)}
                </Typography>
              </Box>
              <Avatar
                sx={{ 
                  width: 36, 
                  height: 36, 
                  bgcolor: getRoleColor(user?.role),
                  fontSize: '0.875rem',
                  fontWeight: 600
                }}
                {...(user?.profilePhoto && { src: user.profilePhoto })}
              >
                {(user?.firstName?.charAt(0) || 'U')?.toUpperCase()}{(user?.lastName?.charAt(0) || '')?.toUpperCase()}
              </Avatar>
              <ExpandMore sx={{ color: 'text.secondary', fontSize: 20 }} />
            </Box>
          </Box>

          {/* Enhanced User Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: {
                width: 280,
                mt: 1,
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
              }
            }}
          >
            {/* User Info Header */}
            <Box sx={{ px: 2, py: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar
                  sx={{ 
                    width: 48, 
                    height: 48, 
                    bgcolor: getRoleColor(user?.role),
                    fontSize: '1rem',
                    fontWeight: 600
                  }}
                  {...(user?.profilePhoto && { src: user.profilePhoto })}
                >
                  {(user?.firstName?.charAt(0) || 'U')?.toUpperCase()}{(user?.lastName?.charAt(0) || '')?.toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {user?.firstName} {user?.lastName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user?.email || 'No email'}
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Divider />
            
            <MenuItem onClick={() => { navigate('/settings'); handleProfileMenuClose(); }} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <Settings fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Settings" secondary="Preferences & security" />
            </MenuItem>
            
            {user?.role === UserRole.ADMIN && (
              <MenuItem onClick={() => { navigate('/admin/settings'); handleProfileMenuClose(); }} sx={{ py: 1.5 }}>
                <ListItemIcon>
                  <AdminPanelSettings fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Organization Settings" secondary="Manage organization" />
              </MenuItem>
            )}
            
            <MenuItem onClick={() => setThemeChoice(mode === 'dark' ? 'light' : 'dark')} sx={{ py: 1.5 }}>
              <ListItemIcon>
                {mode === 'dark' ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
              </ListItemIcon>
              <ListItemText primary={t('menu.darkMode')} />
              <Switch size="small" edge="end" checked={mode === 'dark'} tabIndex={-1} />
            </MenuItem>

            <Divider />
            
            <MenuItem onClick={handleLogout} sx={{ py: 1.5, color: 'error.main' }}>
              <ListItemIcon>
                <Logout fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText 
                primary="Sign Out" 
                secondary="Log out of your account"
                primaryTypographyProps={{ color: 'error.main' }}
              />
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <Toolbar />
        <SupportModeBanner />
        <Outlet />
      </Box>
      <GlobalSearch open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </Box>
  );
};

export default DashboardLayout;
